import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLoan } from "../hooks/useLoan";
import {
  LOAN_STATUS, SLOT_STATUS_META, slotStatus, getSlotNumber, pad2,
  formatCountdown, toDate,
} from "../lib/loans";

const theme = {
  light: {
    bg: "#f8fafc", bg2: "#f1f5f9", bg3: "#e9eef5", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
    green: "#16a34a", greenBg: "#f0fdf4", greenBorder: "#bbf7d0",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    orange: "#ea580c", orangeBg: "#fff7ed", orangeBorder: "#fed7aa",
    cardBg: "#ffffff", shadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#0f172a", bg2: "#131f30", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueBorder: "#1d4ed8",
    green: "#4ade80", greenBg: "#052e16", greenBorder: "#166534",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    orange: "#fb923c", orangeBg: "#2d1a00", orangeBorder: "#92400e",
    cardBg: "#1e293b", shadow: "0 1px 3px rgba(0,0,0,0.4)",
  },
};

const SLOT_COLLECTION = "powerbank_slots";

// Propose la powerbank remise en charge depuis le plus longtemps
const pickMostCharged = (available) =>
  [...available].sort((a, b) =>
    (toDate(a.availableSince)?.getTime() ?? 0) - (toDate(b.availableSince)?.getTime() ?? 0));

// ── Station Canvas (9 emplacements fixes) ─────────────────
function StationCanvas({ slots, highlightNum }) {
  const byNum = Object.fromEntries(slots.map(s => [getSlotNumber(s), s]));

  const labelColor = (num) => {
    if (highlightNum === num) return "#2563eb";
    const s = byNum[num];
    if (!s) return "#94a3b8";
    return SLOT_STATUS_META[slotStatus(s)].color;
  };

  const labels = [
    [1, 880, 490], [2, 510, 490], [3, 140, 490],
    [4, 850, 340], [5, 510, 340], [6, 170, 340],
    [7, 820, 220], [8, 510, 220], [9, 200, 220],
  ];

  return (
    <svg width="100%" viewBox="0 0 1200 1000" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="purpleBase" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#5B2BBF" />
        </linearGradient>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="18" stdDeviation="18" floodColor="#000" floodOpacity="0.22" />
        </filter>
        <symbol id="powerbank" viewBox="0 0 210 390">
          <rect x="0" y="0" width="210" height="390" rx="38" fill="#1a1a1a" />
          <rect x="14" y="18" width="182" height="350" rx="28" fill="none" stroke="#333" strokeWidth="5" />
          <rect x="55" y="10" width="36" height="4" rx="2" fill="#111" />
          <rect x="120" y="10" width="36" height="4" rx="2" fill="#111" />
        </symbol>
      </defs>

      <rect x="115" y="610" width="970" height="170" rx="14" fill="url(#purpleBase)" filter="url(#shadow)" />
      <rect x="160" y="555" width="880" height="120" rx="12" fill="#7C3AED" />
      <g fill="#10051f" opacity="0.65">
        <rect x="185" y="530" width="210" height="70" rx="8" />
        <rect x="495" y="530" width="210" height="70" rx="8" />
        <rect x="805" y="530" width="210" height="70" rx="8" />
      </g>

      <use href="#powerbank" x="190" y="190" width="210" height="390" />
      <use href="#powerbank" x="495" y="190" width="210" height="390" />
      <use href="#powerbank" x="800" y="190" width="210" height="390" />
      <use href="#powerbank" x="160" y="310" width="210" height="390" />
      <use href="#powerbank" x="495" y="310" width="210" height="390" />
      <use href="#powerbank" x="830" y="310" width="210" height="390" />
      <use href="#powerbank" x="125" y="430" width="210" height="390" />
      <use href="#powerbank" x="495" y="430" width="210" height="390" />
      <use href="#powerbank" x="865" y="430" width="210" height="390" />

      <rect x="90" y="710" width="1020" height="190" rx="16" fill="url(#purpleBase)" />
      <rect x="110" y="710" width="980" height="18" fill="#9B6DFF" opacity="0.55" />

      {labels.map(([num, lx, ly]) => {
        const c = labelColor(num);
        const isHL = highlightNum === num;
        const blink = isHL ? { animation: "labelBlink 1s ease infinite" } : {};
        return (
          <g key={num}>
            <rect x={lx} y={ly} width="180" height="60" rx="8" fill={`${c}2e`} stroke={c} strokeWidth={isHL ? 2.5 : 2} style={blink} />
            <circle cx={lx + 23} cy={ly + 20} r="6" fill={c} style={blink} />
            <text x={lx + 95} y={ly + 45} textAnchor="middle" fontSize="40" fontWeight="700" fill={c} fontFamily="monospace" style={blink}>
              PB-{pad2(num)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function ReturnArrows() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setStep(s => (s + 1) % 3), 500);
    return () => clearInterval(i);
  }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", margin: "8px auto 0" }}>
      {[0, 1, 2].map(i => {
        const lit = i <= step;
        return (
          <svg key={i} width="40" height="24" viewBox="0 0 40 24" fill="none" style={{ display: "block", marginTop: i === 0 ? 0 : -6 }}>
            <path d="M4 4L20 20L36 4" stroke={lit ? "#2563eb" : "rgba(37,99,235,0.18)"} strokeWidth={lit ? "4" : "2.5"} strokeLinecap="round" strokeLinejoin="round" style={{ transition: "stroke 0.15s, stroke-width 0.15s" }} />
          </svg>
        );
      })}
    </div>
  );
}

function PowerbankInsert({ slotDisplay }) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setFrame(f => (f + 1) % 4), 500);
    return () => clearInterval(i);
  }, []);
  const offsets = [0, 8, 16, 16];
  const opacities = [1, 1, 1, 0.7];
  return (
    <div style={{ borderRadius: 20, display: "inline-block", width: "100%", position: "relative", overflow: "hidden" }}>
      <img src="/powerbankimage.png" alt="Powerbank" style={{ width: "100%", display: "block", borderRadius: 20, transform: `translateY(${offsets[frame]}px)`, opacity: opacities[frame], transition: frame === 0 ? "transform 0.15s ease-in, opacity 0.15s" : "transform 0.4s ease-out, opacity 0.3s" }} />
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "20%", background: "#2d4a3e", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "0 0 20px 20px" }}>
        <span style={{ color: "#fff", fontSize: 22, fontWeight: 800, letterSpacing: "0.08em", textShadow: "0 1px 4px rgba(0,0,0,0.3)" }}>Slot-{slotDisplay}</span>
      </div>
    </div>
  );
}

export default function PowerBank() {
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [returning, setReturning] = useState(false);
  const t = dark ? theme.dark : theme.light;

  const {
    user, ready, slots, availableSlots, loan, loanSlot, loanMinutes,
    secondsLeft, totalSeconds, overdue, busy, error, clearError, justClosed,
    borrow, reportCondition, declareReturn,
  } = useLoan(SLOT_COLLECTION, { fallbackMinutes: 30, pickSlot: pickMostCharged });

  useEffect(() => { setReturning(false); }, [loan?.id, loan?.status]);

  const toggleTheme = () => setDark(d => {
    localStorage.setItem("ws-theme", !d ? "dark" : "light");
    return !d;
  });

  const slotNum = loan?.slotNumber ?? getSlotNumber(loanSlot);
  const slotDisplay = pad2(slotNum);
  const availableCount = availableSlots.length;

  let view;
  if (!ready) view = "loading";
  else if (!user) view = "login";
  else if (justClosed && !loan) view = "done";
  else if (!loan) view = error === "none" ? "none" : "idle";
  else if (loan.status === LOAN_STATUS.RETURN_PENDING) view = "pending";
  else if (returning) view = "returning";
  else if (!loan.condition) view = "check";
  else view = overdue ? "overdue" : "countdown";

  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const progress = totalSeconds ? (secondsLeft ?? 0) / totalSeconds : 1;
  const strokeDashoffset = circumference * (1 - progress);
  const isUrgent = secondsLeft !== null && secondsLeft < 300;

  const btn = (bg, color = "#fff", extra = {}) => ({
    width: "100%", border: "none", borderRadius: 14, background: bg, color,
    fontSize: 15, fontWeight: 700, padding: 15, cursor: busy ? "wait" : "pointer",
    opacity: busy ? 0.7 : 1, ...extra,
  });

  return (
    <div style={{ background: t.bg, minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pop { 0%{transform:scale(0.85);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-6px)} 75%{transform:translateX(6px)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes labelBlink { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>

      <div style={{ maxWidth: 400, margin: "0 auto", padding: "0 18px", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>
          <img src="/logo.PNG" alt="WeStudy" style={{ height: 28, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={toggleTheme} style={{ width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: t.blue, position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/equipment")} style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingBottom: 60 }}>

          {view === "loading" && <div style={{ textAlign: "center", color: t.text3, fontSize: 13 }}>Chargement…</div>}
          {view === "login" && <div style={{ textAlign: "center", color: t.text2, fontSize: 14 }}>Connectez-vous pour emprunter une powerbank.</div>}

          {/* ── IDLE ── */}
          {view === "idle" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ marginBottom: 16 }}><StationCanvas slots={slots} highlightNum={null} /></div>
              <div style={{ fontSize: 22, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Station Powerbank</div>
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 20, lineHeight: 1.6 }}>
                {availableCount > 0
                  ? `${availableCount} powerbank${availableCount > 1 ? "s" : ""} disponible${availableCount > 1 ? "s" : ""}`
                  : "Aucune powerbank disponible"}
              </div>
              {error && error !== "none" && <div style={{ fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
              <button onClick={borrow} disabled={busy || availableCount === 0}
                style={btn(availableCount > 0 ? t.blue : t.bg3, availableCount > 0 ? "#fff" : t.text3, { fontSize: 16, padding: 16, cursor: availableCount > 0 ? "pointer" : "not-allowed", opacity: availableCount > 0 ? 1 : 0.6 })}>
                ⚡ Recevoir une Powerbank
              </button>
              <div style={{ fontSize: 11, color: t.text3, marginTop: 14 }}>
                {loanMinutes ? `Durée de prêt : ${loanMinutes} minutes • ` : ""}Retour validé par l'accueil
              </div>
            </div>
          )}

          {/* ── CHECK ── */}
          {view === "check" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ marginBottom: 16 }}><StationCanvas slots={slots} highlightNum={slotNum} /></div>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text3, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Prenez la powerbank</div>
              <div style={{ width: 120, height: 120, borderRadius: "50%", background: t.blueBg, border: `3px solid ${t.blue}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", animation: "pop 0.4s ease", boxShadow: `0 0 32px ${t.blue}44` }}>
                <span style={{ fontSize: 54, fontWeight: 900, color: t.blue, lineHeight: 1 }}>{slotDisplay}</span>
              </div>
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 8, lineHeight: 1.6 }}>Récupérez la powerbank <strong style={{ color: t.text1 }}>PB-{slotDisplay}</strong></div>
              {loanMinutes && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: t.orangeBg, border: `1px solid ${t.orangeBorder}`, borderRadius: 20, padding: "4px 12px", marginBottom: 20 }}>
                  <span style={{ fontSize: 12, color: t.orange, fontWeight: 600 }}>⏱ Vous avez {loanMinutes} min pour la rendre</span>
                </div>
              )}
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text2, marginBottom: 12 }}>Elle est en bon état ?</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button disabled={busy} onClick={() => reportCondition("good")} style={btn(t.green, "#fff", { fontSize: 14, padding: 14 })}>✅ Elle fonctionne bien</button>
                <button disabled={busy} onClick={() => reportCondition("defective")} style={btn(t.redBg, t.red, { fontSize: 14, padding: 14, border: `1.5px solid ${t.redBorder}` })}>⚠️ Elle est défectueuse</button>
              </div>
              <div style={{ fontSize: 11, color: t.text3, marginTop: 12 }}>Votre retour aide à maintenir la station en bon état.</div>
            </div>
          )}

          {/* ── COUNTDOWN ── */}
          {view === "countdown" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text3, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>
                {secondsLeft !== null ? "Temps restant" : "Emprunt en cours"}
              </div>
              {secondsLeft !== null && (
                <div style={{ position: "relative", width: 160, height: 160, margin: "0 auto 24px" }}>
                  <svg width="160" height="160" style={{ transform: "rotate(-90deg)" }}>
                    <circle cx="80" cy="80" r={radius} fill="none" stroke={t.bg3} strokeWidth="8" />
                    <circle cx="80" cy="80" r={radius} fill="none" stroke={isUrgent ? t.red : t.blue} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} style={{ transition: "stroke-dashoffset 1s linear, stroke 0.5s" }} />
                  </svg>
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ fontSize: 28, fontWeight: 900, color: isUrgent ? t.red : t.text1, fontVariantNumeric: "tabular-nums", animation: isUrgent ? "pulse 1s infinite" : "none" }}>{formatCountdown(secondsLeft)}</div>
                    <div style={{ fontSize: 10, color: t.text3, marginTop: 2 }}>min:sec</div>
                  </div>
                </div>
              )}
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 28 }}>Remettez la powerbank au slot <strong style={{ color: t.text1 }}>{slotDisplay}</strong> quand vous avez terminé</div>
              <button onClick={() => setReturning(true)} style={btn(t.blue)}>🔋 Rendre la powerbank</button>
            </div>
          )}

          {/* ── OVERDUE ── */}
          {view === "overdue" && (
            <div style={{ animation: "shake 0.4s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.redBg, border: `2px solid ${t.red}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>⚠️</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.red, marginBottom: 8 }}>Temps écoulé !</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 28, lineHeight: 1.6 }}>Veuillez remettre la powerbank au slot <strong style={{ color: t.text1 }}>{slotDisplay}</strong> immédiatement.</div>
              <button onClick={() => setReturning(true)} style={btn(t.red)}>🔋 Rendre la powerbank</button>
            </div>
          )}

          {/* ── RETURNING ── */}
          {view === "returning" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.text3, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 20 }}>Remise en place</div>
              <div style={{ width: 220, margin: "0 auto" }}><PowerbankInsert slotDisplay={slotDisplay} /></div>
              <ReturnArrows />
              <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 6, marginTop: 20 }}>Slot <span style={{ color: t.blue }}>{slotDisplay}</span> — USB vers le bas</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32, lineHeight: 1.6 }}>Appuyez jusqu'au déclic, puis confirmez.<br />L'accueil validera le retour.</div>
              {error && <div style={{ fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button disabled={busy} onClick={() => { clearError(); declareReturn(); }} style={btn(t.green)}>✅ Powerbank remise en place</button>
                <button onClick={() => setReturning(false)} style={btn(t.bg3, t.text2, { fontSize: 13, padding: 11 })}>Annuler</button>
              </div>
            </div>
          )}

          {/* ── PENDING ── */}
          {view === "pending" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ position: "relative", width: 100, height: 100, margin: "0 auto 28px" }}>
                <svg width="100" height="100" style={{ animation: "spin 1.6s linear infinite" }}>
                  <circle cx="50" cy="50" r="42" fill="none" stroke={t.bg3} strokeWidth="6" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={t.orange} strokeWidth="6" strokeLinecap="round" strokeDasharray="70 194" />
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>🔋</div>
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Retour en attente de validation</div>
              <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6 }}>L'accueil va vérifier la powerbank du slot <strong style={{ color: t.text1 }}>{slotDisplay}</strong>.<br />Vous pouvez quitter cette page.</div>
            </div>
          )}

          {/* ── DONE ── */}
          {view === "done" && (
            <div style={{ animation: "pop 0.4s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.greenBg, border: `2px solid ${t.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>✅</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Merci !</div>
              <div style={{ fontSize: 13, color: t.text2 }}>{justClosed === "returned" ? "Retour validé par l'accueil." : "Votre emprunt a été clôturé."}</div>
            </div>
          )}

          {/* ── NONE ── */}
          {view === "none" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>😔</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Aucune powerbank disponible</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32 }}>Quelqu'un vient de prendre la dernière.</div>
              <button onClick={clearError} style={{ border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Réessayer</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}